import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  useCallback,
} from "react"; // Core React
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import { mockCourses } from "shared/services/mockData";
import apiCall from "shared/services/ApiAdapter";
import { useSubscription } from "shared/contexts/Subscription";
import FeatureLimitDisplay from "shared/common/FeatureLimitDisplay";
import KebabMenu from "shared/common/KebabMenu";
import RightDrawer from "shared/common/RightDrawer";
import useStudentEnrollmentData from "shared/hooks/useStudentEnrollmentData";
import { getCurrentUserId, getUserRoleProfileId } from 'shared/utils/SessionDataUtils';
import { useMasterList } from "shared/hooks/useMasterList";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";

// Displays batches for a selected course and pending enrollment requests.
const BatchesContent = ({
  navigateTo,
  selectedCourseTitle,
  selectedCourseId,
  useMockData,
  displayToast,
}) => {
  // State variables for batches and pending requests
  const [batches, setBatches] = useState([]);
  const [pendingEnrollmentRequests, setPendingEnrollmentRequests] = useState(
    []
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { limits, canPerformAction } = useSubscription();

  // Get the tutorId from the session
  const currentUserId = getUserRoleProfileId();
  const userId = getCurrentUserId();

  // Use the custom hook to fetch student enrollment data
  const {
    students,
    enrollments,
    course: fetchedCourse,
    batches: fetchedBatches,
    tutors,
    loading: enrollmentLoading,
    error: enrollmentError,
  } = useStudentEnrollmentData({ courseId: selectedCourseId || null });

  const {
    masterData,
    isLoading: masterDataLoading,
    error: masterDataError
  } = useMasterList({ displayToast, startLoading, stopLoading });

  const getLabelByCode = useCallback((masterData, category, code) => {
    if (!masterData || !masterData[category]) {
      return '';
    }
    const item = masterData[category].find(item => item.value === code);
    return item ? item.label : '';
  }, [masterData]);

  // Use a useEffect hook to update state only when the fetched data changes.
  // This prevents an infinite re-render loop.
  useEffect(() => {
    // Update the batches state with the fetched batches
    if (fetchedBatches) {
      setBatches(fetchedBatches);
    }

    // Filter enrollments directly to get pending requests.
    // This provides access to all enrollment details, including the enrollment ID.
    if (enrollments && students) {
      // Find all enrollments with 'Requested' status
      const pendingRequests = enrollments.filter(
        (enrollment) => enrollment.status === 'Requested'
      );

      // Hydrate the pending requests with full student and course data.
      // This is necessary because the enrollment object might only contain IDs.
      const hydratedRequests = pendingRequests.map(request => {
        // Find the full student object from the students list.
        const student = students.find(s => s.id === request.student.id);

        // Find the full course object from the fetched course.
        // Assuming fetchedCourse is a single object, not an array.
        const course = fetchedCourse;

        // Return a new, enriched object with the full details.
        return {
          ...request,
          student: student || null, // Ensure student is not null
          course: course || null // Ensure course is not null
        };
      });

      setPendingEnrollmentRequests(hydratedRequests);
    }
  }, [fetchedBatches, enrollments, students, fetchedCourse]);

  // Handle student enrollment by navigating to the creation page with all necessary IDs.
  const handleEnrollStudent = (studentId, requestedCourseId, enrollmentId) => {
    navigateTo("createBatchEnrollment", studentId, requestedCourseId, enrollmentId);
  };

  const canCreateBatch = canPerformAction(
    "maxBatchesPerCourse",
    batches.length,
    selectedCourseId
  );
  console.log(canCreateBatch + " can create batch");
  return (
    <div className="p-8 md:px-10">

      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateTo("courses")}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to courses"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              Course - {selectedCourseTitle}
            </span>
          </div>
        }
        subtitle={t("viewManageBatches")}
        rightContentTop={
          <>
            <div className="relative w-full max-w-40">
              <input
                type="text"
                placeholder={t("search")}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                🔍
              </span>
            </div>
            <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
              ⚙️
            </div>
            <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
              <button
                onClick={() => navigateTo("createBatch", selectedCourseTitle, selectedCourseId)}
                className={`px-5 py-2 rounded-lg font-medium transition-colors ${canCreateBatch
                  ? "bg-gray-800 text-gray-100 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400"
                  }`}
                disabled={!canCreateBatch}
              >
                {t("createBatch")}
              </button>
            </ProtectedVisibility>
          </>
        }
        rightContentBottom={<div className="flex flex-col items-end">
          <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
            <FeatureLimitDisplay
              feature="maxBatchesPerCourse"
              currentCount={batches.length}
              limit={limits.maxBatchesPerCourse}
            />
          </ProtectedVisibility>
        </div>}
      />

      <div className="flex flex-col md:flex-row gap-8">
        {/* Batches List Section */}
        <div
          className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
          style={{ flexBasis: isDrawerOpen ? "50%" : "calc(100% - 2rem)" }}
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 sm:mb-0">
              {t("myBatches")}
            </h2>
          </div>

          <div className="space-y-4">
            {batches.length === 0 ? (
              <div className="text-center text-gray-600 dark:text-gray-400">
                {t("noBatchesFound")}
              </div>
            ) : (
              batches.map((batch) => (
                batchCard(batch, userId, navigateTo, selectedCourseTitle, selectedCourseId, t)
              ))
            )}
          </div>
        </div>

        {/* Pending Enrollment Requests Drawer */}
        <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>

          <RightDrawer
            title="pendingEnrollmentRequests"
            count={pendingEnrollmentRequests.length}
            isOpen={isDrawerOpen}
            toggleDrawer={() => setIsDrawerOpen(!isDrawerOpen)}
          >
            <ul className="space-y-4">
              {pendingEnrollmentRequests.length === 0 ? (
                <div className="text-center text-gray-600 dark:text-gray-400">
                  {t("noPendingRequests")}
                </div>
              ) : (
                pendingEnrollmentRequests.map((enrollment) => (
                  <li
                    key={enrollment.id}
                    className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4"
                  >
                    <div className="flex items-center">
                      <img
                        src={
                          enrollment.student?.profilePictureUrl ||
                          `https://placehold.co/40x40/e0f2f7/00796b?text=${enrollment.student?.name ? enrollment.student.name.charAt(0) : '?'}`
                        }
                        alt={enrollment.student?.name}
                        className="w-10 h-10 rounded-full mr-3"
                      />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 leading-tight">
                          {enrollment.student?.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <p className="text-sm text-gray-600 dark:text-gray-400">{getLabelByCode(masterData, 'academicClasses', enrollment.student?.academicClass)}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{getLabelByCode(masterData, 'curricula', enrollment.student?.curriculum)}</p>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleEnrollStudent(
                          enrollment.student?.id,
                          enrollment.course?.id,
                          enrollment.id
                        )
                      }
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                    >
                      {t("enroll")}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </RightDrawer>
        </ProtectedVisibility>
      </div>
    </div>
  );
};

export default BatchesContent;
function batchCard(batch, userId, navigateTo, selectedCourseTitle, selectedCourseId, t) {
  const cardDetails = (
    <>
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {batch.batchName}
        </h3>
      </div>
      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
        {batch.academicClass} {batch.subject} |{" "}
        {batch.startDate
          ? new Date(batch.startDate).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
          : ""}{" "}
        -{" "}
        {batch.endDate
          ? new Date(batch.endDate).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
          : ""}{" "}
        | {batch.maxCapacity} {t("students").toLowerCase()}
      </p>
    </>
  );

  return (userId === batch.createdBy) ? (
    <div
      key={batch.id}
      className="relative border-b border-gray-200 dark:border-gray-700 pb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md p-2 -mx-2"
      onClick={() =>
        navigateTo("studentRoster", selectedCourseTitle, selectedCourseId, batch.id)
      }
    >
      <div className="absolute top-0 right-0 z-10">
        <KebabMenu
          onEdit={() =>
            navigateTo("updateBatch", selectedCourseTitle, selectedCourseId, batch.id)
          }
          onEvaluations={() =>
            navigateTo("batchEvaluations", selectedCourseTitle, selectedCourseId, batch.id)
          }
        />
      </div>
      <div className="pr-10">{cardDetails}</div>
    </div>
  ) : (
    <div
      key={batch.id}
      className="relative border-b border-gray-200 dark:border-gray-700 pb-4 rounded-md p-2 -mx-2"
    >
      <div className="absolute top-0 right-0 z-10" />
      <div className="pr-10">{cardDetails}</div>
    </div>
  );
}


